/**
 * Homepage hero variant registry.
 *
 * Source-of-truth doc: docs/03-funnel/homepage-message-testing.md
 * Technical design:    docs/04-technical/homepage-variants-design.md
 * ADR (system):        docs/09-adr/adr-008-homepage-message-testing.md
 * ADR (Sprint 5):      docs/09-adr/adr-013-sprint5-registry-refresh.md
 * ADR (Sprint 9):      docs/09-adr/adr-017-sprint9-analytics-hero-copy-inline-quiz-preview-style.md
 * ADR (Sprint 10):     docs/09-adr/adr-018-sprint10-v1-v5-copy-refresh.md
 *
 * DO NOT add hero copy anywhere else in the codebase. Components must
 * render headline, subheadline, CTA, and reassurance from this file via
 * the `HomepageVariant` shape. Adding or changing a variant is a one-PR
 * change: edit the doc table in homepage-message-testing.md §6, update
 * this registry, run `pnpm typecheck && pnpm lint && pnpm test`.
 *
 * Sprint 5 (ADR-013) replaced the V0–V12 set with V0–V5 and added the
 * `future-savings` angle. The replacement was design-driven, not lift-
 * driven; see ADR-013 for the reasoning.
 */

export const ANGLES = [
  "money-leaks",
  "month-end",
  "archetype",
  "save-more",
  "future-savings",
] as const;

export type Angle = (typeof ANGLES)[number];

export interface HomepageVariant {
  /** Globally unique id. "V0" is the canonical default. Format: V<n>. */
  readonly id: string;
  /** One of the documented angles. */
  readonly angle: Angle;
  /** One sentence. No exclamation. ≤ 80 chars. */
  readonly headline: string;
  /** ≤ 2 sentences, ≤ 180 chars. */
  readonly subheadline: string;
  /** Imperative verb. ≤ 28 chars. */
  readonly cta: string;
  /**
   * Reassurance microcopy under the CTA. `null` means inherit the
   * canonical V0 reassurance. Default to null unless there's a specific
   * measurement reason to vary it.
   */
  readonly reassurance: string | null;
  /** Internal one-liner about why this variant exists. Never rendered. */
  readonly notes: string;
}

export const CANONICAL_VARIANT_ID = "V0" as const;

/**
 * V0 is the canonical default. Always at index 0. Served to:
 *   - Search engines (the only indexable variant).
 *   - Visitors with no `?variant=` / `?angle=` query parameter.
 *   - Visitors whose parameter does not resolve to a known variant.
 *
 * V1–V5 are Sprint 5 test cells. Their `reassurance` is `null` so they
 * inherit V0's reassurance line — that keeps the comparison fair across
 * cells. Sprint anchor variants per angle (first match in registry):
 *   - future-savings → V0
 *   - archetype      → V2
 *   - money-leaks    → V3
 *   - month-end      → V4
 *   - save-more      → V5
 */
export const HOMEPAGE_VARIANTS: ReadonlyArray<HomepageVariant> = [
  {
    id: "V0",
    angle: "future-savings",
    headline:
      "The average user could save $3,247 a year by understanding their money habits.",
    subheadline:
      "A 5-minute audit names your money archetype and shows you exactly where the leaks are.",
    cta: "Start my free audit",
    reassurance:
      "5 min · 20 questions · no signup to see your archetype.",
    notes:
      "Sprint 9 canonical (ADR-017 §B.1). Realigned to the locked $3,247/year hero anchor from ADR-016; drops the Sprint 5 $250/mo framing. Carries the locked landing CTA verbatim.",
  },
  {
    id: "V1",
    angle: "future-savings",
    headline: "$3,247 a year is what the average user is leaking. Find your share.",
    subheadline:
      "We name your archetype and the three leaks costing you that much, then show which one to plug first.",
    cta: "Start the audit",
    reassurance: null,
    notes:
      "Sprint 10 (ADR-018 §V1). Loss-anchored inversion of V0's save-anchored framing on the same locked $3,247 figure. Tests pure framing direction; same angle as V0 so the comparison isolates direction, not topic.",
  },
  {
    id: "V2",
    angle: "archetype",
    headline: "Which of the 7 money archetypes is yours?",
    subheadline:
      "Each archetype leaks differently. Yours has three specific patterns you can name in five minutes.",
    cta: "Reveal my archetype",
    reassurance: null,
    notes:
      "Sprint 10 (ADR-018 §V2). Question-form archetype anchor with the locked 7-archetype count. Replaces Sprint 5's 'find out which' phrasing with a more direct invitation; preserves the archetype angle.",
  },
  {
    id: "V3",
    angle: "money-leaks",
    headline: "Three leaks. One archetype. $3,247 a year on the line.",
    subheadline:
      "A five-minute audit names the pattern and prices the cost of leaving it running for another twelve months.",
    cta: "Run my audit",
    reassurance: null,
    notes:
      "Sprint 10 (ADR-018 §V3). Three-leak / one-archetype framing held from Sprint 5; now grounded in the locked annual figure. Mirrors the result-page three-finding grid vocabulary.",
  },
  {
    id: "V4",
    angle: "month-end",
    headline: "Broke by the 28th? This names the leak that did it.",
    subheadline:
      "The audit pinpoints which of your archetype's three patterns is draining the month first — and what to try next.",
    cta: "Show me the leak",
    reassurance: null,
    notes:
      "Sprint 10 (ADR-018 §V4). Date-specific concreteness ('the 28th'), sharper than the Sprint 5 'why' framing. CTA mirrors the three-leak vocabulary; subheadline references the archetype's three patterns explicitly.",
  },
  {
    id: "V5",
    angle: "save-more",
    headline: "What you'd actually keep if you plugged the biggest leak.",
    subheadline:
      "We name your archetype, count its biggest leak, and give you one habit to try this week.",
    cta: "See what I'd keep",
    reassurance: null,
    notes:
      "Sprint 10 (ADR-018 §V5). Keep-not-cut framing held from Sprint 5. Subheadline anchors on 'one habit this week' to match the result page's One Next Step section.",
  },
];

/**
 * Motivational microcopy pool — see homepage-message-testing.md §7.
 * IDs are stable identifiers used by analytics; lines must stay in sync
 * with the doc. The unit test in homepage-variants.test.ts holds the
 * canonical id→line mapping and will fail if either side drifts.
 *
 * Where these lines may appear is governed by the doc; do NOT inline them
 * into components without checking the rules.
 */
export interface MicrocopyLine {
  readonly id: string;
  readonly line: string;
}

export const MICROCOPY_POOL: ReadonlyArray<MicrocopyLine> = [
  { id: "mc_pattern_clearer", line: "Every answer makes the pattern clearer." },
  {
    id: "mc_costing_most",
    line: "By the end, you'll see which habit is costing you the most.",
  },
  { id: "mc_no_judgment", line: "This is about spotting the pattern, not judging it." },
  {
    id: "mc_few_steps",
    line: "You're a few steps away from seeing what deserves your attention first.",
  },
  { id: "mc_clarity_not_guilt", line: "The goal isn't guilt. It's clarity." },
  { id: "mc_one_next_step", line: "At the end, you'll get one practical next step." },
  {
    id: "mc_one_insight",
    line: "Better money habits often start with one honest insight.",
  },
  {
    id: "mc_top_three_leaks",
    line: "You're getting closer to your top 3 money leaks.",
  },
];

// ----------------------------------------------------------------------
// Lookups
// ----------------------------------------------------------------------

/** Return the canonical default variant. Throws only if the registry was mangled. */
export function getCanonicalVariant(): HomepageVariant {
  const v = HOMEPAGE_VARIANTS.find((x) => x.id === CANONICAL_VARIANT_ID);
  if (!v) {
    // This would mean the registry has been edited to remove V0 — a
    // configuration error. Throwing here keeps the failure loud at
    // build/test time rather than silently shipping a broken homepage.
    throw new Error(
      `Homepage variant registry is missing canonical id "${CANONICAL_VARIANT_ID}".`,
    );
  }
  return v;
}

/** Get a variant by id, case-insensitive. Returns undefined if not found. */
export function findVariantById(id: string): HomepageVariant | undefined {
  const upper = id.trim().toUpperCase();
  return HOMEPAGE_VARIANTS.find((v) => v.id.toUpperCase() === upper);
}

/**
 * Return the *anchor* variant for an angle. The anchor is the first
 * variant in the registry whose `angle` matches. By construction (V0 for
 * future-savings, V2 for archetype, V3 for money-leaks, V4 for month-end,
 * V5 for save-more) the anchor is the canonical entry per angle for
 * Sprint 5.
 */
export function findAnchorVariantByAngle(angle: Angle): HomepageVariant {
  const v = HOMEPAGE_VARIANTS.find((x) => x.angle === angle);
  if (!v) {
    // ANGLES and HOMEPAGE_VARIANTS are co-defined; a missing anchor means
    // the registry drifted from the Angle type. Loud failure preferred.
    throw new Error(`No anchor variant in registry for angle "${angle}".`);
  }
  return v;
}

/**
 * Return the effective reassurance line for a variant. Variants with a
 * null reassurance inherit the canonical V0 reassurance. V0's own
 * reassurance is non-null by registry invariant.
 */
export function effectiveReassurance(v: HomepageVariant): string {
  if (v.reassurance !== null) return v.reassurance;
  const canonicalReassurance = getCanonicalVariant().reassurance;
  if (canonicalReassurance === null) {
    throw new Error(
      "Canonical variant must have a non-null reassurance line. " +
        "Fix the registry, then this branch becomes unreachable.",
    );
  }
  return canonicalReassurance;
}
